import SEO from "@/components/SEO";
import { FadeIn, MaskedLines, Chapter } from "@/components/motion";
import CtaSection from "@/components/CtaSection";
import { SUPPORT_SERVICES } from "@/data/content";

export default function PatientSupport() {
  return (
    <>
      <SEO
        title="International Patient Assistance in India | GetWellSoon"
        description="Airport pickup, hotel and accommodation assistance, interpreter support, appointment scheduling, local transportation, and family support services in India."
      />

      <section className="bg-pearl" data-testid="patient-support-hero">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <Chapter index="01" label="Patient support" />
          <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:items-end">
            <MaskedLines
              delay={0.1}
              className="font-serif text-4xl leading-[1.06] tracking-tight text-ink sm:text-5xl lg:col-span-8 lg:text-6xl"
              lines={[
                "On the ground,",
                <span key="l2">
                  so you are never <span className="italic text-brand">on your own</span>.
                </span>,
              ]}
            />
            <FadeIn delay={0.4} className="lg:col-span-4">
              <p className="text-base leading-relaxed text-muted-foreground">
                Treatment abroad involves a hundred small logistics. We handle
                them — for the patient, and for the family alongside them.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="patient-support-services">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SUPPORT_SERVICES.slice(0, 3).map((s, i) => (
              <ServiceCard key={s.title} s={s} i={i} />
            ))}
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-12">
            <FadeIn className="lg:col-span-7" data-testid="support-image-1">
              <div className="h-full overflow-hidden rounded-[2rem] rounded-tr-[4rem] border border-border">
                <img
                  src="/images/airport-arrival.jpg"
                  alt="An international flight arriving at a modern airport terminal"
                  className="h-72 w-full object-cover md:h-80"
                />
              </div>
            </FadeIn>
            <FadeIn delay={0.12} className="lg:col-span-5" data-testid="support-image-2">
              <div className="h-full overflow-hidden rounded-[2rem] rounded-bl-[4rem] border border-border">
                <img
                  src="/images/family-support.jpg"
                  alt="A nurse caring for a senior patient with warmth"
                  className="h-72 w-full object-cover md:h-80"
                />
              </div>
            </FadeIn>
          </div>

          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SUPPORT_SERVICES.slice(3).map((s, i) => (
              <ServiceCard key={s.title} s={s} i={i + 3} />
            ))}
          </div>
        </div>
      </section>

      <CtaSection />
    </>
  );
}

function ServiceCard({ s, i }) {
  return (
    <FadeIn
      delay={(i % 3) * 0.08}
      className="group rounded-3xl border border-border bg-white p-8 transition-colors duration-300 hover:border-brand/40"
      data-testid={`support-service-${i}`}
    >
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sage-light text-sage-dark transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
        <s.icon className="h-5 w-5" />
      </span>
      <h2 className="mt-6 font-serif text-xl tracking-tight text-ink">
        {s.title}
      </h2>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
        {s.text}
      </p>
    </FadeIn>
  );
}
